import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Loader2, ChevronDown, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { useGlobalCities } from '@/hooks/useGlobalCities';
import { debugLogger } from '@/utils/debugLogger';

interface CitySelectProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string | null;
  stateCode: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CitySelect: React.FC<CitySelectProps> = ({
  value,
  onChange,
  countryCode,
  stateCode,
  placeholder = "Selecione a cidade",
  disabled = false,
  className = ''
}) => {
  const { cities, isLoading, error } = useGlobalCities(countryCode, stateCode);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Determine if we should show manual input mode (no cities available)
  const hasCities = cities.length > 0;
  const showManualInput = !isLoading && stateCode && !hasCities && !error;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Filter cities based on search term
  const filteredCities = useMemo(() => {
    if (!cities.length) return [];
    if (!searchTerm.trim()) return cities;
    
    const normalizedSearch = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    return cities.filter(city => {
      const normalizedName = city.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return normalizedName.includes(normalizedSearch);
    });
  }, [cities, searchTerm]);

  const handleSelect = (cityName: string) => {
    onChange(cityName);
    setIsOpen(false);
    setSearchTerm('');
    debugLogger.debug('CitySelect', `Selected city: ${cityName}`);
  };

  const handleTriggerClick = () => {
    if (!disabled && !isLoading && stateCode) {
      setIsOpen(!isOpen);
    }
  };

  const getDisplayValue = () => {
    if (isLoading) return 'Carregando cidades...';
    if (!stateCode) return 'Primeiro selecione o estado';
    if (error) return 'Erro ao carregar cidades';
    if (value) return value;
    return placeholder;
  };

  const isDisabled = disabled || isLoading || !stateCode;

  // Manual input mode when no cities available for this region
  if (showManualInput) {
    return (
      <div className={className}>
        <div className="relative">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Digite o nome da sua cidade"
            disabled={disabled}
            className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
            data-testid="city-manual-input"
          />
        </div>
        <p className="text-xs text-white/50 mt-1.5 flex items-center gap-1">
          <span>ℹ️</span>
          <span>Lista de cidades indisponível para esta região</span>
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={isDisabled}
        className={`
          flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm
          bg-white/5 border-white/20 text-white
          focus:outline-none focus:ring-2 focus:ring-luxury-pinkBright/50 focus:border-luxury-pinkBright/50
          disabled:cursor-not-allowed disabled:opacity-50
          ${isOpen ? 'ring-2 ring-luxury-pinkBright/50 border-luxury-pinkBright/50' : ''}
        `}
        data-testid="city-select-trigger"
      >
        <span className={value ? 'text-white' : 'text-white/40'}>
          {getDisplayValue()}
        </span>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-white/60" />
        ) : (
          <ChevronDown className={`h-4 w-4 text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && stateCode && !isLoading && (
        <Card className="absolute z-50 w-full mt-1 bg-gray-900 border-white/20 shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                ref={searchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar cidade..."
                className="pl-8 h-9 bg-white/5 border-white/20 text-white placeholder:text-white/40 text-sm"
                data-testid="city-search-input"
              />
            </div>
          </div>

          {/* Cities list */}
          <div className="max-h-[250px] overflow-y-auto">
            {filteredCities.length === 0 ? (
              <div className="p-3 text-sm text-white/60 text-center">
                {searchTerm ? 'Nenhuma cidade encontrada' : 'Nenhuma cidade disponível'}
              </div>
            ) : (
              <div className="py-1">
                {filteredCities.map((city) => (
                  <button
                    key={city.name}
                    type="button"
                    className={`
                      w-full px-3 py-2 text-left text-sm transition-colors
                      hover:bg-white/10 focus:bg-white/10 focus:outline-none
                      ${city.name === value ? 'bg-white/10 text-white font-medium' : 'text-white/80'}
                    `}
                    onClick={() => handleSelect(city.name)}
                    data-testid={`city-option-${city.name}`}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer with count */}
          {cities.length > 0 && (
            <div className="px-3 py-2 text-xs text-white/40 text-center border-t border-white/10">
              {filteredCities.length} de {cities.length} cidades
            </div>
          )}
        </Card>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-1">{error.message}</p>
      )}
    </div>
  );
};

export default CitySelect;
